import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CategoriaCampoCustomizado, CategoriaCampoFormData } from '@/types/categoria-campo';
import { useAuth } from './useAuth';
import { useClientContext } from '@/contexts/ClientContext';

export const useCategoriaCampos = (categoriaId?: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentClientId } = useClientContext();

  // Buscar campos por categoria
  const { data: campos = [], isLoading } = useQuery({
    queryKey: ['categoria-campos', categoriaId],
    queryFn: async () => {
      if (!categoriaId) return [];
      
      const { data, error } = await supabase
        .from('categoria_campos_customizados')
        .select('*')
        .eq('categoria_id', categoriaId)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      if (error) throw error;
      return data as CategoriaCampoCustomizado[];
    },
    enabled: !!categoriaId,
  });

  // Criar novo campo
  const createCampo = useMutation({
    mutationFn: async (data: CategoriaCampoFormData & { categoria_id: string }) => {
      const { data: result, error } = await supabase
        .from('categoria_campos_customizados')
        .insert({
          categoria_id: data.categoria_id,
          nome_campo: data.nome_campo,
          label: data.label,
          tipo_campo: data.tipo_campo,
          descricao: data.descricao,
          obrigatorio: data.obrigatorio,
          ordem: data.ordem,
          configuracoes: data.configuracoes as any,
          ativo: data.ativo,
          client_id: currentClientId || null,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-campos'] });
      toast.success('Campo criado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao criar campo:', error);
      toast.error('Erro ao criar campo customizado');
    },
  });

  // Atualizar campo
  const updateCampo = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CategoriaCampoFormData> }) => {
      const updateData: any = {};
      if (data.nome_campo !== undefined) updateData.nome_campo = data.nome_campo;
      if (data.label !== undefined) updateData.label = data.label;
      if (data.tipo_campo !== undefined) updateData.tipo_campo = data.tipo_campo;
      if (data.descricao !== undefined) updateData.descricao = data.descricao;
      if (data.obrigatorio !== undefined) updateData.obrigatorio = data.obrigatorio;
      if (data.ordem !== undefined) updateData.ordem = data.ordem;
      if (data.configuracoes !== undefined) updateData.configuracoes = data.configuracoes;
      if (data.ativo !== undefined) updateData.ativo = data.ativo;

      const { data: result, error } = await supabase
        .from('categoria_campos_customizados')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-campos'] });
      toast.success('Campo atualizado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao atualizar campo:', error);
      toast.error('Erro ao atualizar campo customizado');
    },
  });

  // Deletar campo
  const deleteCampo = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('categoria_campos_customizados')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-campos'] });
      toast.success('Campo removido com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao deletar campo:', error);
      toast.error('Erro ao remover campo customizado');
    },
  });

  return {
    campos,
    isLoading,
    createCampo,
    updateCampo,
    deleteCampo,
  };
};
